
import React, { useState, useEffect } from "react";
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    serverTimestamp,
    doc,
    updateDoc,
    deleteDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import "./DocVault.css";

const CATEGORIES = [
    { id: 'all', name: 'All Documents', icon: 'bi-files', color: 'primary' },
    { id: 'company', name: 'Company Docs', icon: 'bi-building', color: 'info' },
    { id: 'financial', name: 'Banking Records', icon: 'bi-currency-rupee', color: 'success' },
    { id: 'employee', name: 'Employees', icon: 'bi-people', color: 'warning' },
    { id: 'legal', name: 'Legal & Compliance', icon: 'bi-shield-check', color: 'danger' },
    { id: 'customer', name: 'Customer Contracts', icon: 'bi-person-badge', color: 'secondary' }
];

export default function DocumentVault() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("grid"); // grid or list

    // Upload State
    const [showUpload, setShowUpload] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadForm, setUploadForm] = useState({
        title: "",
        category: "company",
        expiryDate: "",
        tags: "",
        relatedTo: ""
    });

    // Preview State
    const [previewDoc, setPreviewDoc] = useState(null);

    useEffect(() => {
        fetchDocuments();
    }, [activeCategory]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            let q = collection(db, "DOCUMENT_VAULT");
            if (activeCategory !== 'all') {
                q = query(q, where("category", "==", activeCategory));
            }
            const snap = await getDocs(q);
            const docList = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Client-side sorting to avoid composite index requirement
            docList.sort((a, b) => {
                const timeA = a.uploadedAt?.seconds || 0;
                const timeB = b.uploadedAt?.seconds || 0;
                return timeB - timeA;
            });

            setDocs(docList);
        } catch (err) {
            console.error("Error fetching documents:", err);
            toast.error("Failed to load documents");
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        const file = e.target.files?.[0] || e.dataTransfer?.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            toast.error("File too large (Max 10MB)");
            return;
        }

        setUploading(true);
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `document_vault/${uploadForm.category}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(Math.round(progress));
            },
            (error) => {
                console.error("Upload failed:", error);
                toast.error("Upload failed");
                setUploading(false);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                const docData = {
                    title: uploadForm.title || file.name,
                    category: uploadForm.category,
                    fileUrl: downloadURL,
                    fileName: fileName,
                    fileType: file.type,
                    fileSize: (file.size / 1024 / 1024).toFixed(2) + " MB",
                    uploadedBy: auth.currentUser?.email || "Admin",
                    uploadedAt: serverTimestamp(),
                    expiryDate: uploadForm.expiryDate || null,
                    tags: uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean),
                    status: "active",
                    version: 1
                };

                const docRef = await addDoc(collection(db, "DOCUMENT_VAULT"), docData);

                // Audit Log
                await addDoc(collection(db, "DOC_ACTIVITY_LOGS"), {
                    action: "UPLOAD",
                    docId: docRef.id,
                    docTitle: docData.title,
                    user: auth.currentUser?.email || "Admin",
                    timestamp: serverTimestamp()
                });

                toast.success("Document uploaded successfully");
                setUploading(false);
                setUploadProgress(0);
                setShowUpload(false);
                fetchDocuments();
            }
        );
    };

    const handleDelete = async (docObj) => {
        if (!window.confirm("Are you sure you want to delete this document?")) return;

        try {
            // 1. Delete from Storage
            const storageRef = ref(storage, `document_vault/${docObj.category}/${docObj.fileName}`);
            await deleteObject(storageRef);

            // 2. Delete from Firestore
            await deleteDoc(doc(db, "DOCUMENT_VAULT", docObj.id));

            // 3. Audit Log
            await addDoc(collection(db, "DOC_ACTIVITY_LOGS"), {
                action: "DELETE",
                docId: docObj.id,
                docTitle: docObj.title,
                user: auth.currentUser?.email || "Admin",
                timestamp: serverTimestamp()
            });

            toast.success("Document deleted permanently");
            fetchDocuments();
        } catch (err) {
            console.error("Delete failed:", err);
            toast.error("Failed to delete document");
        }
    };

    const handleDownload = async (docItem) => {
        // Log activity
        await addDoc(collection(db, "DOC_ACTIVITY_LOGS"), {
            action: "DOWNLOAD",
            docId: docItem.id,
            docTitle: docItem.title,
            user: auth.currentUser?.email || "Admin",
            timestamp: serverTimestamp()
        });
        window.open(docItem.fileUrl, "_blank");
    };

    const filteredDocs = docs.filter(d =>
        d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const checkExpiry = (date) => {
        if (!date) return null;
        const now = new Date();
        const exp = new Date(date);
        const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { status: 'expired', label: 'Expired', color: 'danger' };
        if (diffDays < 30) return { status: 'warning', label: `Expires in ${diffDays}d`, color: 'warning' };
        return null;
    };

    return (
        <div className="container-fluid py-4 doc-vault-container">
            <div className="row g-4 mb-4">
                <div className="col-md-9">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold mb-1">Document Vault</h3>
                            <p className="text-muted small">Manage secure business documents and compliance records</p>
                        </div>
                        <div className="d-flex gap-2">
                            <div className="input-group input-group-sm" style={{ width: '250px' }}>
                                <span className="input-group-text bg-white border-end-0"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Search documents..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={() => setShowUpload(true)}>
                                <i className="bi bi-cloud-arrow-up"></i> Upload
                            </button>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm p-3">
                                <h6 className="fw-bold mb-3 px-2">Folders</h6>
                                {CATEGORIES.map(cat => (
                                    <div
                                        key={cat.id}
                                        className={`sidebar-category d-flex align-items-center justify-content-between ${activeCategory === cat.id ? 'active' : ''}`}
                                        onClick={() => setActiveCategory(cat.id)}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <i className={`bi ${cat.icon}`}></i>
                                            <span>{cat.name}</span>
                                        </div>
                                        <span className="badge rounded-pill bg-light text-dark small">
                                            {cat.id === 'all' ? docs.length : docs.filter(d => d.category === cat.id).length}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="col-md-9">
                            <div className="d-flex justify-content-end mb-3 gap-2">
                                <button className={`btn btn-sm ${viewMode === 'grid' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setViewMode('grid')}>
                                    <i className="bi bi-grid-fill"></i>
                                </button>
                                <button className={`btn btn-sm ${viewMode === 'list' ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setViewMode('list')}>
                                    <i className="bi bi-list-ul"></i>
                                </button>
                            </div>

                            {loading ? <Loader /> : (
                                <div className="row g-3">
                                    {viewMode === 'grid' ? (
                                        filteredDocs.map(docItem => {
                                            const expiry = checkExpiry(docItem.expiryDate);
                                            return (
                                                <div className="col-md-4 animate-in" key={docItem.id}>
                                                    <div className="card h-100 file-card border-0 shadow-sm">
                                                        <div className="card-body text-center p-4">
                                                            {expiry && (
                                                                <span className={`badge bg-${expiry.color} badge-expiry`}>
                                                                    {expiry.label}
                                                                </span>
                                                            )}
                                                            <div className={`file-icon text-${CATEGORIES.find(c => c.id === docItem.category)?.color || 'primary'}`}>
                                                                <i className={`bi ${docItem.fileType.includes('pdf') ? 'bi-file-pdf' : (docItem.fileType.includes('image') ? 'bi-file-image' : 'bi-file-earmark-text')}`}></i>
                                                            </div>
                                                            <h6 className="fw-bold text-truncate mb-1" title={docItem.title}>{docItem.title}</h6>
                                                            <p className="x-small text-muted mb-3">{docItem.fileSize} • {new Date(docItem.uploadedAt?.seconds * 1000).toLocaleDateString()}</p>

                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <button className="btn btn-sm btn-outline-primary" onClick={() => setPreviewDoc(docItem)}>View</button>
                                                                <button className="btn btn-sm btn-outline-success" onClick={() => handleDownload(docItem)}>Download</button>
                                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(docItem)}>
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm overflow-hidden">
                                                <table className="table table-hover mb-0">
                                                    <thead className="table-light">
                                                        <tr className="small">
                                                            <th>Document Name</th>
                                                            <th>Category</th>
                                                            <th>Uploaded At</th>
                                                            <th>Size</th>
                                                            <th>Expiry</th>
                                                            <th className="text-end">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredDocs.map(docItem => (
                                                            <tr key={docItem.id} className="align-middle">
                                                                <td className="fw-bold">
                                                                    <i className={`bi bi-file-earmark me-2 text-primary`}></i>
                                                                    {docItem.title}
                                                                </td>
                                                                <td><span className="badge bg-light text-dark">{docItem.category}</span></td>
                                                                <td className="small">{new Date(docItem.uploadedAt?.seconds * 1000).toLocaleDateString()}</td>
                                                                <td className="small text-muted">{docItem.fileSize}</td>
                                                                <td>
                                                                    {(() => {
                                                                        const e = checkExpiry(docItem.expiryDate);
                                                                        return e ? <span className={`badge bg-${e.color} x-small`}>{e.label}</span> : <span className="text-muted small">-</span>;
                                                                    })()}
                                                                </td>
                                                                <td className="text-end">
                                                                    <button className="btn btn-sm btn-link p-0 me-3" onClick={() => setPreviewDoc(docItem)}>View</button>
                                                                    <button className="btn btn-sm btn-link p-0 text-danger" onClick={() => handleDelete(docItem)}>Delete</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                    {filteredDocs.length === 0 && (
                                        <div className="text-center py-5">
                                            <i className="bi bi-folder2-open display-4 text-muted opacity-50"></i>
                                            <p className="mt-3 text-muted">No documents found in this folder</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card border-0 shadow-sm p-4 h-100">
                        <h6 className="fw-bold mb-4">Document Stats</h6>
                        <div className="mb-4">
                            <label className="small text-muted mb-1 d-block">Used Storage</label>
                            <h4 className="fw-bold">{(docs.reduce((acc, d) => acc + parseFloat(d.fileSize), 0)).toFixed(2)} MB</h4>
                            <div className="progress mt-2" style={{ height: '6px' }}>
                                <div className="progress-bar bg-primary" style={{ width: '15%' }}></div>
                            </div>
                        </div>

                        <hr />

                        <h6 className="fw-bold mb-3 small">Recent Notifications</h6>
                        <div className="d-flex flex-column gap-3">
                            {docs.filter(d => checkExpiry(d.expiryDate)).slice(0, 3).map(d => {
                                const e = checkExpiry(d.expiryDate);
                                return (
                                    <div className="p-2 bg-light rounded d-flex align-items-center gap-3" key={d.id}>
                                        <i className={`bi bi-exclamation-circle-fill text-${e.color}`}></i>
                                        <div>
                                            <div className="fw-bold small">{d.title}</div>
                                            <div className="x-small text-muted">{e.label}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {docs.filter(d => checkExpiry(d.expiryDate)).length === 0 && (
                                <div className="text-center py-4 text-muted small">No pending alerts</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* UPLOAD MODAL */}
            {showUpload && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold">New Document Upload</h5>
                                <button className="btn-close" onClick={() => setShowUpload(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Document Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. GST Registration 2024"
                                            value={uploadForm.title}
                                            onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Category</label>
                                            <select
                                                className="form-select"
                                                value={uploadForm.category}
                                                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                                            >
                                                {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Expiry Date (Optional)</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={uploadForm.expiryDate}
                                                onChange={(e) => setUploadForm({ ...uploadForm, expiryDate: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <label className="form-label small fw-bold">Tags (comma separated)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="legal, 2024, confidential"
                                            value={uploadForm.tags}
                                            onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                                        />
                                    </div>

                                    {!uploading ? (
                                        <div className="dropzone" onClick={() => document.getElementById('vaultFileInput').click()}>
                                            <i className="bi bi-cloud-arrow-up display-6 text-primary"></i>
                                            <h6 className="mt-2 fw-bold">Click to select</h6>
                                            <p className="text-muted x-small">PDF, Image or Docs (Max 10MB)</p>
                                            <input
                                                type="file"
                                                id="vaultFileInput"
                                                className="d-none"
                                                onChange={handleUpload}
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <h6 className="fw-bold mb-3">Uploading {uploadProgress}%</h6>
                                            <div className="progress" style={{ height: '10px' }}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}
            {previewDoc && (
                <div className="modal fade show d-block preview-modal" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0">
                            <div className="modal-header bg-dark text-white border-0 py-2">
                                <h6 className="modal-title small">{previewDoc.title}</h6>
                                <button className="btn-close btn-close-white" onClick={() => setPreviewDoc(null)}></button>
                            </div>
                            <div className="modal-body p-0">
                                <div className="preview-container">
                                    {previewDoc.fileType.includes('pdf') ? (
                                        <iframe src={previewDoc.fileUrl} width="100%" height="600px" title="PDF Preview"></iframe>
                                    ) : (
                                        <img src={previewDoc.fileUrl} className="img-fluid" alt="Preview" style={{ maxHeight: '80vh' }} />
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer bg-light py-2">
                                <small className="text-muted me-auto">Uploaded by {previewDoc.uploadedBy} on {new Date(previewDoc.uploadedAt?.seconds * 1000).toLocaleString()}</small>
                                <button className="btn btn-sm btn-primary" onClick={() => handleDownload(previewDoc)}>Download Original</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

